import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import CookieService from "../../context/cookies";

import { base } from "../../constants/apis";
import {
  DefaultFlowTemplateData,
  DefaultFlowTemplateRes,
} from "../../types/FlowTemplates";

export const flowTemplatesApi = createApi({
  reducerPath: "flowTemplatesApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${base}/apis/flow_template`,
    prepareHeaders: (headers) => {
      const token = CookieService.getCookie("jwtToken");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      headers.set("Content-Type", "application/json");
      return headers;
    },
  }),
  tagTypes: ["flowTemplatesApi"],

  endpoints: (builder) => ({
    getDefaultFlowTemplates: builder.query<DefaultFlowTemplateData[], void>({
      query: () => "/get_default_template.php",
      transformResponse: (res: DefaultFlowTemplateRes) => res.data,
    }),
    getFlowTemplates: builder.query<DefaultFlowTemplateData[], void>({
      query: () => "/get.php",
      transformResponse: (res: DefaultFlowTemplateRes) => res.data,
      providesTags: ["flowTemplatesApi"],
    }),

    addFlowTemplate: builder.mutation({
      query: (payload) => ({
        url: "/add.php",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["flowTemplatesApi"],
    }),
    updateFlowTemplate: builder.mutation({
      query: (payload) => ({
        url: "/update.php",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["flowTemplatesApi"],
    }),

    deleteFlowTemplate: builder.mutation({
      query: (id) => ({
        url: "/delete.php",
        method: "POST",
        body: { id },
      }),
      invalidatesTags: ["flowTemplatesApi"],

      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          flowTemplatesApi.util.updateQueryData(
            "getFlowTemplates",
            undefined,
            (draft) => {
              const tableIndex = draft.findIndex((el) => el.id === id);
              draft.splice(tableIndex, 1);
            }
          )
        );

        try {
          await queryFulfilled;
        } catch (error) {
          patchResult.undo();
        }
      },
    }),

    // ------ access control -----

    getAccessControlFlowTemplates: builder.query<
      DefaultFlowTemplateData[],
      { parent_id: number }
    >({
      query: (payload) => ({
        url: "/access_control/get.php",
        method: "POST",
        body: payload,
      }),
      transformResponse: (res: DefaultFlowTemplateRes) => res.data,
    }),
  }),
});

export const {
  useGetDefaultFlowTemplatesQuery,
  useGetFlowTemplatesQuery,
  useAddFlowTemplateMutation,
  useUpdateFlowTemplateMutation,
  useDeleteFlowTemplateMutation,

  useGetAccessControlFlowTemplatesQuery,
} = flowTemplatesApi;
